import { v4 } from '@rotra-air-ocean/rotranext-shared-code';

/** A field known to exist in the data context, with its type and (enum) values. */
export interface FieldInfo {
  name: string;
  type: v4.FieldInputType;
  options?: string[];
  /** Item schema for an array field. */
  items?: FieldInfo;
  /** Property schema for an object field. */
  properties?: FieldInfo[];
  /** Step id that produces it, or "email" for the always-seeded email fields. */
  producedBy: string;
  /** Human-readable source name for context pickers. */
  producedByLabel?: string;
  /** Kind of the step that produced this field, when applicable. */
  producedByKind?: v4.StepKind;
}

const EMAIL_ATTACHMENT_FIELDS: FieldInfo[] = [
  { name: 'attachmentId', type: 'text', producedBy: 'email' },
  { name: 'name', type: 'text', producedBy: 'email' },
  { name: 'contentType', type: 'text', producedBy: 'email' },
  { name: 'size', type: 'number', producedBy: 'email' },
  { name: 'isInline', type: 'checkbox', producedBy: 'email' },
  { name: 'contentId', type: 'text', producedBy: 'email' },
  {
    name: 'kind',
    type: 'select',
    options: ['file', 'item', 'reference'],
    producedBy: 'email',
  },
  {
    name: 'itemType',
    type: 'select',
    options: ['message', 'event', 'contact'],
    producedBy: 'email',
  },
  {
    name: 'status',
    type: 'select',
    options: ['pending', 'processing', 'processed', 'unsupported', 'failed'],
    producedBy: 'email',
  },
  { name: 'markdownAvailable', type: 'checkbox', producedBy: 'email' },
  { name: 'textAvailable', type: 'checkbox', producedBy: 'email' },
  { name: 'parentAttachmentId', type: 'text', producedBy: 'email' },
  { name: 'nestingDepth', type: 'number', producedBy: 'email' },
  { name: 'sourceOrder', type: 'number', producedBy: 'email' },
  { name: 'markdownCharacters', type: 'number', producedBy: 'email' },
  { name: 'textCharacters', type: 'number', producedBy: 'email' },
  {
    name: 'processingWarnings',
    type: 'array',
    producedBy: 'email',
    items: {
      name: 'warning',
      type: 'object',
      producedBy: 'email',
      properties: [
        { name: 'code', type: 'text', producedBy: 'email' },
        { name: 'message', type: 'text', producedBy: 'email' },
        { name: 'omittedCount', type: 'number', producedBy: 'email' },
      ],
    },
  },
  { name: 'error', type: 'text', producedBy: 'email' },
];

const EMAIL_ATTACHMENTS_FIELD: FieldInfo = {
  name: 'emailAttachments',
  type: 'array',
  producedBy: 'email',
  items: {
    name: 'attachment',
    type: 'object',
    producedBy: 'email',
    properties: EMAIL_ATTACHMENT_FIELDS,
  },
};

/** Email fields seeded into context.data when a run starts (always available). */
const EMAIL_CONTEXT_FIELD_DEFINITIONS: FieldInfo[] = [
  { name: 'emailId', type: 'text', producedBy: 'email' },
  { name: 'emailSubject', type: 'text', producedBy: 'email' },
  { name: 'emailBody', type: 'text', producedBy: 'email' },
  { name: 'emailFrom', type: 'email', producedBy: 'email' },
  { name: 'emailFromName', type: 'text', producedBy: 'email' },
  { name: 'mailbox', type: 'text', producedBy: 'email' },
  { name: 'receivedAt', type: 'text', producedBy: 'email' },
  EMAIL_ATTACHMENTS_FIELD,
  {
    name: 'emailConversation',
    type: 'array',
    producedBy: 'email',
    items: {
      name: 'email',
      type: 'object',
      producedBy: 'email',
      properties: [
        { name: 'emailSubject', type: 'text', producedBy: 'email' },
        { name: 'emailBody', type: 'text', producedBy: 'email' },
        { name: 'emailFrom', type: 'email', producedBy: 'email' },
        { name: 'emailFromName', type: 'text', producedBy: 'email' },
        { name: 'mailbox', type: 'text', producedBy: 'email' },
        { name: 'receivedAt', type: 'text', producedBy: 'email' },
        EMAIL_ATTACHMENTS_FIELD,
      ],
    },
  },
];

export const EMAIL_CONTEXT_FIELDS: FieldInfo[] =
  EMAIL_CONTEXT_FIELD_DEFINITIONS.map((field) => ({
    ...field,
    producedByLabel: 'Incoming email',
  }));

const EMAIL_CONTEXT_FIELD_NAMES = new Set(
  EMAIL_CONTEXT_FIELDS.map((field) => field.name),
);

/** A compact human-readable representation for context and mapping affordances. */
export function fieldTypeLabel(
  field: Pick<FieldInfo, 'type' | 'options' | 'items' | 'properties'>,
): string {
  if (field.type === 'array') {
    return `array<${field.items ? fieldTypeLabel(field.items) : 'unknown'}>`;
  }
  if (field.type === 'object') {
    return `object { ${(field.properties ?? [])
      .map((property) => `${property.name}: ${fieldTypeLabel(property)}`)
      .join(', ')} }`;
  }
  return `${field.type}${
    field.options ? ` [${field.options.join(', ')}]` : ''
  }`;
}

/** A placeholder value standing in for a field's real value, shown in preview JSON. */
function previewValueForField(field: FieldInfo): unknown {
  switch (field.type) {
    case 'number':
      return 0;
    case 'checkbox':
      return false;
    case 'array':
      return field.items ? [previewValueForField(field.items)] : [];
    case 'object':
      return Object.fromEntries(
        (field.properties ?? []).map((property) => [
          property.name,
          previewValueForField(property),
        ]),
      );
    case 'select':
      return field.options?.[0] ?? '…';
    case 'email':
      return 'name@example.com';
    default:
      return `<${field.name}>`;
  }
}

/** The `state` JSON object a systemOne step would send for the selected fields. */
export function buildStatePreview(
  fields: FieldInfo[],
  selectedNames: string[],
): Record<string, unknown> {
  const byName = new Map(fields.map((field) => [field.name, field]));
  const names =
    selectedNames.length > 0 ? selectedNames : fields.map((f) => f.name);
  return Object.fromEntries(
    names.map((name) => [
      name,
      byName.has(name) ? previewValueForField(byName.get(name)!) : `<${name}>`,
    ]),
  );
}

/** Fields a step adds to the context when it completes (declared producers only). */
export function producedBy(stepId: string, step: v4.StepSpec): FieldInfo[] {
  const sourceLabel = step.label?.trim() || 'A previous step';
  const fromFields = (fields: v4.FieldSpec[]): FieldInfo[] =>
    (fields ?? [])
      .filter((f) => f.name)
      .map((f) => ({
        name: f.name,
        type: f.inputType,
        options: f.options,
        items: f.items
          ? fieldInfoFromSpec(f.items, stepId, sourceLabel, step.kind)
          : undefined,
        properties: f.properties?.map((property) =>
          fieldInfoFromSpec(property, stepId, sourceLabel, step.kind),
        ),
        producedBy: stepId,
        producedByLabel: sourceLabel,
        producedByKind: step.kind,
      }));
  if (step.kind === 'userInput') {
    return fromFields(step.fields);
  }
  if (step.kind === 'llm') {
    return fromFields(step.output);
  }
  if (step.kind === 'systemOne') {
    return Object.entries(step.questions).map(([questionId, question]) => ({
      name: questionId,
      type: 'object' as v4.FieldInputType,
      properties: systemOneAnswerProperties(question.type, stepId, sourceLabel),
      producedBy: stepId,
      producedByLabel: sourceLabel,
      producedByKind: step.kind,
    }));
  }

  if (step.kind === 'subWorkflow') {
    return Object.values(step.outputs ?? {})
      .filter(Boolean)
      .map((parentName) => ({
        name: parentName,
        type: 'text' as v4.FieldInputType,
        producedBy: stepId,
        producedByLabel: sourceLabel,
        producedByKind: step.kind,
      }));
  }
  return [];
}

/** Shape of a System One answer, which varies by question type. */
function systemOneAnswerProperties(
  type: v4.SystemOneQuestionSpec['type'],
  stepId: string,
  sourceLabel: string,
): FieldInfo[] {
  const field = (name: string, fieldType: v4.FieldInputType): FieldInfo => ({
    name,
    type: fieldType,
    producedBy: stepId,
    producedByLabel: sourceLabel,
  });
  switch (type) {
    case 'choice':
      return [
        field('choice', 'text'),
        field('confidence', 'number'),
        field('probabilities', 'object'),
      ];
    case 'score':
      return [
        field('score', 'number'),
        field('confidence', 'number'),
        field('legend', 'object'),
        field('probabilities', 'object'),
      ];
    case 'noul':
      return [field('noul', 'number')];
  }
}

function fieldInfoFromSpec(
  field: v4.FieldSpec,
  sourceId: string,
  producedByLabel?: string,
  producedByKind?: v4.StepKind,
): FieldInfo {
  return {
    name: field.name,
    type: field.inputType,
    options: field.options,
    items: field.items
      ? fieldInfoFromSpec(
          field.items,
          sourceId,
          producedByLabel,
          producedByKind,
        )
      : undefined,
    properties: field.properties?.map((property) =>
      fieldInfoFromSpec(property, sourceId, producedByLabel, producedByKind),
    ),
    producedBy: sourceId,
    producedByLabel,
    producedByKind,
  };
}

/** The transition targets declared by a step. */
export function transitionTargets(step: v4.StepSpec): string[] {
  switch (step.kind) {
    case 'auto':
    case 'apiCall':
    case 'outlookCategory':
    case 'userInput':
    case 'llm':
    case 'systemOne': {
      return step.next ? [step.next] : [];
    }
    case 'decision': {
      return step.actions.map((a) => a.next);
    }
    case 'branch': {
      return [
        ...Object.values(step.cases ?? {}),
        ...(step.default ? [step.default] : []),
      ];
    }
    case 'subWorkflow': {
      return step.onDone ? [step.onDone] : [];
    }
    default: {
      return [];
    }
  }
}

/** Labelled transitions for display in the steps list. */
export function stepTransitions(
  step: v4.StepSpec,
): { label: string; target: string }[] {
  switch (step.kind) {
    case 'auto':
    case 'apiCall':
    case 'outlookCategory':
    case 'llm':
    case 'systemOne':
    case 'userInput': {
      return step.next ? [{ label: '→', target: step.next }] : [];
    }
    case 'decision': {
      return step.actions.map((a) => ({ label: a.label, target: a.next }));
    }
    case 'branch': {
      return [
        ...Object.entries(step.cases ?? {}).map(([k, t]) => ({
          label: k || '?',
          target: t,
        })),
        ...(step.default ? [{ label: 'default', target: step.default }] : []),
      ];
    }
    case 'subWorkflow': {
      return step.onDone ? [{ label: 'done →', target: step.onDone }] : [];
    }
    default: {
      return [];
    }
  }
}

/** Step ids that can reach `target` (its ancestors), excluding itself. */
export function ancestorsOf(
  spec: v4.WorkflowSpec,
  target: string,
): Set<string> {
  const reverse = new Map<string, string[]>();
  for (const [id, step] of Object.entries(spec.steps)) {
    for (const t of transitionTargets(step)) {
      const arr = reverse.get(t) ?? [];
      arr.push(id);
      reverse.set(t, arr);
    }
  }
  const seen = new Set<string>();
  const queue = [...(reverse.get(target) ?? [])];
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (seen.has(id)) {
      continue;
    }
    seen.add(id);
    for (const p of reverse.get(id) ?? []) {
      if (!seen.has(p)) {
        queue.push(p);
      }
    }
  }
  seen.delete(target);
  return seen;
}

/** True if any ancestor produces untracked fields (service results we can't enumerate). */
export function hasUntrackedAncestor(
  spec: v4.WorkflowSpec,
  target: string,
): boolean {
  for (const id of ancestorsOf(spec, target)) {
    const kind = spec.steps[id]?.kind;
    if (kind === 'auto' || kind === 'apiCall') {
      return true;
    }
  }
  return false;
}

/** Fields available in the data context on entry to `stepId`. */
export function availableFieldsAt(
  _trigger: 'email' | 'workflow',
  spec: v4.WorkflowSpec,
  stepId: string,
): FieldInfo[] {
  const map = new Map<string, FieldInfo>();
  for (const f of EMAIL_CONTEXT_FIELDS) {
    map.set(f.name, f);
  }

  if (spec.inputs) {
    for (const f of spec.inputs) {
      if (EMAIL_CONTEXT_FIELD_NAMES.has(f.name)) {
        continue;
      }
      map.set(f.name, {
        name: f.name,
        type: f.inputType,
        options: f.options,
        items: f.items
          ? fieldInfoFromSpec(f.items, '(workflow inputs)', 'Workflow inputs')
          : undefined,
        properties: f.properties?.map((property) =>
          fieldInfoFromSpec(property, '(workflow inputs)', 'Workflow inputs'),
        ),
        producedBy: '(workflow inputs)',
        producedByLabel: 'Workflow inputs',
      });
    }
  }

  for (const id of ancestorsOf(spec, stepId)) {
    const step = spec.steps[id];
    if (!step) {
      continue;
    }
    for (const f of producedBy(id, step)) {
      const existing = map.get(f.name);
      // Prefer a producer that carries enum options.
      if (!existing || (!existing.options && f.options)) {
        map.set(f.name, f);
      }
    }
  }
  return [...map.values()];
}

export function findField(
  fields: FieldInfo[],
  name: string | undefined,
): FieldInfo | undefined {
  // Select controls currently store top-level context keys. Template paths are
  // deliberately resolved separately so a nested scalar is not accidentally
  // treated as a supported branch or LLM input selection.
  return name ? fields.find((field) => field.name === name) : undefined;
}

/**
 * A path segment in the small template language. String segments select object
 * properties; number segments select an item from a list.
 */
export type TemplatePathSegment = v4.TemplatePathSegment;

/** The result of parsing a reference before checking it against workflow data. */
export type ParsedTemplatePath = v4.ParsedTemplatePath;

/**
 * Re-export the shared parser so UI controls use the exact same grammar as the
 * workflow runtime. It accepts `list[0].field` and normalises legacy
 * `list.0.field` to bracket-index syntax.
 */
export const parseTemplatePath = v4.parseTemplatePath;

/** Why a parsed template path did not resolve against this step's data schema. */
export type TemplatePathErrorCode =
  | 'invalid-syntax'
  | 'unknown-root'
  | 'missing-object-schema'
  | 'unknown-property'
  | 'object-index'
  | 'missing-array-schema'
  | 'array-property'
  | 'scalar-child';

/**
 * A template path after it has been checked against the data available at a
 * workflow step. `field` exists only when the full path is valid.
 */
export interface TemplatePathResolution {
  /** The reference as written by the user, without surrounding `{{` / `}}`. */
  ref: string;
  /** The documented bracket-index form when the reference has valid syntax. */
  canonicalPath?: string;
  /** Parsed path segments. Array indexes are numbers. */
  segments?: TemplatePathSegment[];
  /** The top-level context field that this path starts from, when known. */
  root?: FieldInfo;
  /** The exact field selected by the complete path, when known. */
  field?: FieldInfo;
  /** True only when syntax and every schema segment are valid. */
  known: boolean;
  /** A concise, user-facing explanation when `known` is false. */
  reason?: string;
  errorCode?: TemplatePathErrorCode;
}

/**
 * Extra fields available only in a particular template, such as an LLM
 * summary's own outputs. `string[]` remains supported for existing callers;
 * use `FieldInfo[]` when their nested schema is known.
 */
export type TemplatePathExtra = FieldInfo | string;

/**
 * Resolve a reference such as `customer.address.city` or
 * `emailConversation[0].emailSubject` against a set of available fields.
 *
 * This is deliberately schema-aware: an object requires a named property and
 * a list requires an index before its item fields can be selected.
 */
export function resolveTemplatePath(
  fields: FieldInfo[],
  reference: string | undefined,
  extra?: readonly TemplatePathExtra[],
): TemplatePathResolution {
  const ref = reference?.trim() ?? '';
  const parsed = parseTemplatePath(ref);
  if (!parsed.ok) {
    return {
      ref,
      known: false,
      reason: parsed.reason,
      errorCode: 'invalid-syntax',
    };
  }

  const segments = parsed.segments!;
  const fieldsByName = new Map<string, FieldInfo>();
  for (const field of fields) {
    fieldsByName.set(field.name, field);
  }
  for (const field of extra ?? []) {
    const extraField =
      typeof field === 'string'
        ? ({
            name: field,
            type: 'text' as v4.FieldInputType,
            producedBy: '(template-only)',
          } satisfies FieldInfo)
        : field;
    // A template-specific field represents the value at the point this
    // template runs, so it deliberately wins over an earlier field of the same
    // name (for example an LLM output in that LLM's summary).
    fieldsByName.set(extraField.name, extraField);
  }

  const rootName = segments[0];
  if (typeof rootName !== 'string') {
    // The shared parser always produces a string first segment. Keeping this
    // guard makes the schema walker safe if that invariant ever changes.
    return {
      ref,
      canonicalPath: parsed.canonicalPath,
      segments,
      known: false,
      reason: 'A reference must start with a field name.',
      errorCode: 'invalid-syntax',
    };
  }

  const root = fieldsByName.get(rootName);
  if (!root) {
    return {
      ref,
      canonicalPath: parsed.canonicalPath,
      segments,
      known: false,
      reason: `No data named “${rootName}” is available at this step.`,
      errorCode: 'unknown-root',
    };
  }

  let current = root;
  for (let index = 1; index < segments.length; index += 1) {
    const segment = segments[index];
    const parentPath = v4.templatePathToString(segments.slice(0, index));

    if (current.type === 'object') {
      if (typeof segment === 'number') {
        return unresolvedTemplatePath({
          ref,
          parsed,
          root,
          reason: `“${parentPath}” is an object. Choose one of its named fields instead of an array index.`,
          errorCode: 'object-index',
        });
      }
      if (!current.properties?.length) {
        return unresolvedTemplatePath({
          ref,
          parsed,
          root,
          reason: `“${parentPath}” has no defined object fields, so “${segment}” cannot be checked.`,
          errorCode: 'missing-object-schema',
        });
      }
      const property = current.properties.find(
        (field) => field.name === segment,
      );
      if (!property) {
        return unresolvedTemplatePath({
          ref,
          parsed,
          root,
          reason: `“${parentPath}” has no field named “${segment}”.`,
          errorCode: 'unknown-property',
        });
      }
      current = property;
      continue;
    }

    if (current.type === 'array') {
      if (typeof segment !== 'number') {
        return unresolvedTemplatePath({
          ref,
          parsed,
          root,
          reason: `“${parentPath}” is a list. Choose an item first, for example “${parentPath}[0]”.`,
          errorCode: 'array-property',
        });
      }
      if (!current.items) {
        return unresolvedTemplatePath({
          ref,
          parsed,
          root,
          reason: `“${parentPath}” has no defined list item schema, so nested fields cannot be checked.`,
          errorCode: 'missing-array-schema',
        });
      }
      current = current.items;
      continue;
    }

    return unresolvedTemplatePath({
      ref,
      parsed,
      root,
      reason: `“${parentPath}” is ${fieldTypeLabel(
        current,
      )}, so it has no nested fields.`,
      errorCode: 'scalar-child',
    });
  }

  return {
    ref,
    canonicalPath: parsed.canonicalPath,
    segments,
    root,
    field: current,
    known: true,
  };
}

function unresolvedTemplatePath({
  ref,
  parsed,
  root,
  reason,
  errorCode,
}: {
  ref: string;
  parsed: ParsedTemplatePath;
  root: FieldInfo;
  reason: string;
  errorCode: Exclude<TemplatePathErrorCode, 'invalid-syntax' | 'unknown-root'>;
}): TemplatePathResolution {
  return {
    ref,
    canonicalPath: parsed.canonicalPath,
    segments: parsed.segments,
    root,
    known: false,
    reason,
    errorCode,
  };
}

const TEMPLATE_RE = v4.TEMPLATE_REFERENCE_RE;

/** A segment of a template string: either plain text or a `{{token}}`. */
export interface TemplateToken {
  text: string;
  /** The raw inner reference for token segments (e.g. "emailSubject"). */
  ref?: string;
  /** The documented bracket-index form of `ref`, when its syntax is valid. */
  canonicalPath?: string;
  /** Parsed segments for a token. Array indexes are numbers. */
  segments?: TemplatePathSegment[];
  /** The exact referenced field when the complete path is valid. */
  field?: FieldInfo;
  /** A precise explanation when the reference is not valid at this step. */
  reason?: string;
  /** Whether the complete path resolves to an available context field. */
  known?: boolean;
}

/**
 * Split a template string into plain-text and `{{token}}` segments, marking each
 * token as known/unknown against the available field set. Used for template
 * validation and the used-data indicators in the editor.
 */
export function classifyTemplateTokens(
  value: string,
  fields: FieldInfo[],
  /** Extra fields (e.g. an LLM step's own outputs in its summary). */
  extra?: readonly TemplatePathExtra[],
): TemplateToken[] {
  const out: TemplateToken[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  TEMPLATE_RE.lastIndex = 0;
  while ((m = TEMPLATE_RE.exec(value))) {
    if (m.index > last) {
      out.push({ text: value.slice(last, m.index) });
    }
    const resolution = resolveTemplatePath(fields, m[1], extra);
    out.push({
      text: m[0],
      ref: resolution.ref,
      canonicalPath: resolution.canonicalPath,
      segments: resolution.segments,
      field: resolution.field,
      reason: resolution.reason,
      known: resolution.known,
    });
    last = m.index + m[0].length;
  }
  if (last < value.length) {
    out.push({ text: value.slice(last) });
  }
  return out;
}

function templateRefs(...values: (string | undefined)[]): string[] {
  const out: string[] = [];
  for (const v of values) {
    if (!v) {
      continue;
    }
    let m: RegExpExecArray | null;
    TEMPLATE_RE.lastIndex = 0;
    while ((m = TEMPLATE_RE.exec(v))) {
      out.push(m[1].trim());
    }
  }
  return out;
}

/**
 * Non-blocking warnings for a step's field references: unknown fields (only when
 * no untracked producer is upstream, to avoid false positives), branch case
 * values that aren't members of the selected enum field, and unsafe branch
 * routing configuration.
 */
export function stepWarnings(
  trigger: 'email' | 'workflow',
  spec: v4.WorkflowSpec,
  stepId: string,
  step: v4.StepSpec,
): string[] {
  const fields = availableFieldsAt(trigger, spec, stepId);
  const untracked = hasUntrackedAncestor(spec, stepId);
  const warnings: string[] = [];

  const checkRef = (
    reference: string | undefined,
    where: string,
    extra?: readonly TemplatePathExtra[],
  ) => {
    if (!reference) {
      return;
    }
    const resolution = resolveTemplatePath(fields, reference, extra);
    // An untracked service may provide a root field that the editor cannot
    // enumerate. It cannot make an otherwise malformed path valid, though.
    if (
      !resolution.known &&
      !(untracked && resolution.errorCode === 'unknown-root')
    ) {
      warnings.push(
        `${where} references "${reference}". ${
          resolution.reason ?? 'that data is not available at this step'
        }`,
      );
    }
  };

  if (step.kind === 'branch') {
    checkRef(step.selectField, 'Select field');
    const selectedField = resolveTemplatePath(fields, step.selectField);
    const field = findField(fields, step.selectField);
    const selectedPath = selectedField.canonicalPath ?? step.selectField;
    if ((selectedField.segments?.length ?? 0) > 1) {
      warnings.push(
        `Select field "${selectedPath}" is nested. Branch steps can only route using a top-level field.`,
      );
    }
    if (field?.type === 'array' || field?.type === 'object') {
      warnings.push(
        `Select field "${selectedPath}" is an ${field.type} and cannot be routed directly. Choose a scalar field instead.`,
      );
    }
    if (!step.default) {
      warnings.push(
        'This branch has no fallback. Unmatched or unexpected values will fail the workflow.',
      );
    }
    if (field?.options) {
      for (const key of Object.keys(step.cases ?? {})) {
        if (key && !field.options.includes(key)) {
          warnings.push(
            `Case "${key}" is not a value of ${selectedPath} (${field.options.join(
              ', ',
            )}).`,
          );
        }
      }
    }
  }
  if (step.kind === 'llm' || step.kind === 'systemOne') {
    const selectedNames =
      step.kind === 'llm' ? step.inputFields ?? [] : step.stateFields ?? [];
    const label = step.kind === 'llm' ? 'Input field' : 'State field';
    for (const name of selectedNames) {
      const resolution = resolveTemplatePath(fields, name);
      if (resolution.known && (resolution.segments?.length ?? 0) > 1) {
        warnings.push(
          `${label} "${
            resolution.canonicalPath ?? name
          }" is nested. Choose its whole top-level field instead.`,
        );
      } else {
        checkRef(name, label);
      }
    }
  }

  if (
    (step.kind === 'llm' ||
      step.kind === 'systemOne' ||
      step.kind === 'auto' ||
      step.kind === 'apiCall') &&
    step.documents?.selector.type === 'contextField'
  ) {
    const selectorField = findField(fields, step.documents.selector.field);
    if (!selectorField && !untracked) {
      warnings.push(
        `Document selector field "${step.documents.selector.field}" is not available at this step.`,
      );
    } else if (
      selectorField &&
      (selectorField.type !== 'array' || selectorField.items?.type !== 'text')
    ) {
      warnings.push(
        `Document selector field "${step.documents.selector.field}" must be an array of attachment ids.`,
      );
    }
  }

  if (step.kind === 'outlookCategory' && !step.category?.trim()) {
    warnings.push(
      'Category to set is required. Enter a category name or insert a value that is available at this step.',
    );
  }

  // Pre-output templates see the entry context only.
  for (const ref of templateRefs(
    'activity' in step ? step.activity : undefined,
    'prompt' in step ? step.prompt : undefined,
    'message' in step ? step.message : undefined,
    'category' in step ? step.category : undefined,
    ...Object.values('defaults' in step ? step.defaults ?? {} : {}),
  )) {
    checkRef(ref, 'Template');
  }

  // An llm/systemOne step's summary runs after the model returns, so it may
  // also use the step's own output fields.
  const ownOutputs =
    step.kind === 'llm' || step.kind === 'systemOne'
      ? producedBy('(this step)', step).map((field) => ({
          ...field,
          producedByLabel: 'This step’s output',
        }))
      : undefined;
  for (const ref of templateRefs(
    'summary' in step ? step.summary : undefined,
  )) {
    checkRef(ref, 'Summary', ownOutputs);
  }

  return warnings;
}
